'use client';
import { useParams, useSearchParams } from 'next/navigation';
import { type ChangeEvent, useEffect, useMemo, useState } from 'react';

import { CollapsePanelButton } from '@/app/components/shared/dealer/components/shared/collapse-panel-button';
import { FilterOptions } from '@/app/components/shared/dealer/components/shared/filter-options';
import { SearchPanelAdvancedSearch } from '@/app/components/shared/dealer/components/shared/search-panel/advanced-search';
import { SearchPanelDealerDetail } from '@/app/components/shared/dealer/components/shared/search-panel/dealer-detail';
import { SearchListDealersLocation } from '@/app/components/shared/dealer/components/shared/search-panel/list-dealers-location';
import { useDealerSearchKey } from '@/app/components/shared/dealer/hooks/useDealerSearchKey';
import styles from '@/app/components/shared/dealer/styles.module.css';
import {
  AdvancedSearch,
  DEALER_SEARCH_KEY,
  SearchType,
} from '@/app/components/shared/dealer/utils/dealer-constant';
import type {
  DealerLocation,
  DealerSearch,
} from '@/app/components/shared/dealer/utils/types';
import { useDisclosure } from '@/app/hooks/useDisclosure';
import { cn } from '@/app/utils/cn';
import { safeCall } from '@/app/utils/safeCall';

interface SearchPanelProps {
  className?: string;
  dealers?: DealerSearch[];
  selectedDealer?: DealerSearch | null;
  searchValue?: string;
  searchType?: SearchType;
  setFromMarker?: (value: boolean) => void;
  setDealerLocation?: (location: DealerLocation) => void;
  setSearchValue?: (value: string) => void;
  setSearchFilter?: (value: string[]) => void;
  setSelectDealer?: (value?: DealerSearch | null) => void;
  setSearchType?: (value: SearchType) => void;
  onClickCurrentLocation: () => void;
  onClearSearchType?: () => void;
}

export const SearchPanel = ({
  className,
  dealers = [],
  selectedDealer,
  searchValue,
  searchType,
  setFromMarker,
  setDealerLocation,
  setSearchValue,
  setSearchFilter,
  setSelectDealer,
  setSearchType,
  onClickCurrentLocation,
  onClearSearchType,
}: SearchPanelProps) => {
  const params = useParams();
  const searchParams = useSearchParams();

  const {
    allSearchKey,
    initVehicleType,
    removeSearchLocation,
    getSelectDealerUrl,
    getAdvancedSearchKey,
    handleAdvancedSearch,
  } = useDealerSearchKey();

  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(
    new Set()
  );
  const [isOpenFilter, { close: onCloseFilter, toggle: onToggleFilter }] =
    useDisclosure(false);
  const [isOpenCollapsePanel, { toggle: toggleCollapsePanel }] =
    useDisclosure(true);

  useEffect(() => {
    const value = initVehicleType(params?.uid as string);
    if (value) {
      setSelectedOptions((prev) => {
        const newSet = new Set(prev);
        newSet.add(value);
        return newSet;
      });
    }
    getAdvancedSearchKey(setSelectedOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    safeCall(setSearchFilter, Array.from(selectedOptions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);

  const onCloseSearchPanel = (isDetailDealer?: boolean) => {
    if (isOpenFilter) {
      onCloseFilter();
    }

    if (params?.uid || isDetailDealer) {
      safeCall(setSelectDealer, undefined);
      removeSearchLocation();
    }
  };

  const onSelectDealer = (dealer: DealerSearch, selectedTab?: string) => {
    const url = getSelectDealerUrl(dealer, selectedTab, searchValue);
    safeCall(setSelectDealer, dealer);
    safeCall(setFromMarker, false);
    return setTimeout(() => {
      window.history.pushState(null, '', url);
    }, 0);
  };

  const onSelectType = (options: string[]) => {
    setSelectedOptions(new Set(options));
  };

  const handleSelectFilterOption = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    if (searchType !== SearchType.NATURAL) {
      safeCall(setSearchType, SearchType.NATURAL);
    }

    setSelectedOptions((prev) => {
      const newSet = new Set(prev);

      if (name === DEALER_SEARCH_KEY.Service) {
        if (checked) {
          newSet.add(name);
        } else {
          newSet.delete(name);
          newSet.delete(DEALER_SEARCH_KEY.ChangeTire);
          newSet.delete(DEALER_SEARCH_KEY.Overhaul);
        }
        return newSet;
      }

      if (
        [DEALER_SEARCH_KEY.ChangeTire, DEALER_SEARCH_KEY.Overhaul].includes(
          name as DEALER_SEARCH_KEY
        )
      ) {
        if (checked) {
          newSet.add(DEALER_SEARCH_KEY.Service);
        } else {
          newSet.delete(DEALER_SEARCH_KEY.Service);
        }
      }

      if (checked) {
        newSet.add(name);
      } else {
        newSet.delete(name);
      }
      return newSet;
    });
  };

  const onResetFilterSearch = (type?: AdvancedSearch) => {
    if (type?.options && type.options.length > 0) {
      for (const option of type.options) {
        setSelectedOptions((prev) => {
          const newSet = new Set(prev);
          newSet.delete(option.name);
          return newSet;
        });
      }
      return safeCall(setSelectDealer, null);
    }

    setSelectedOptions(new Set());
    safeCall(setSelectDealer, null);
  };

  const onFilterSearch = () => {
    if (selectedOptions.size === 0 && searchParams?.size === 0) {
      return onCloseFilter();
    }

    const currentParams = handleAdvancedSearch(Array.from(selectedOptions));

    onSelectType(Array.from(selectedOptions));
    onCloseFilter();

    setTimeout(() => {
      window.history.pushState(
        null,
        '',
        `/dealer/search?${currentParams.toString()}`
      );
    }, 0);
  };

  const renderContent = useMemo(() => {
    if (isOpenFilter) {
      return (
        <SearchPanelAdvancedSearch
          onSelectOptions={handleSelectFilterOption}
          onClose={onCloseFilter}
          onReset={onResetFilterSearch}
          onSearch={onFilterSearch}
          selectedFilterSearch={selectedOptions}
        />
      );
    }

    if (selectedDealer) {
      return (
        <SearchPanelDealerDetail
          dealer={selectedDealer}
          onClose={onCloseSearchPanel}
          activeTab={allSearchKey[DEALER_SEARCH_KEY.Tab] ?? ''}
          activeDetailTab={allSearchKey[DEALER_SEARCH_KEY.VehicleType] ?? ''}
          onClickCurrentLocation={onClickCurrentLocation}
        />
      );
    }

    return (
      <SearchListDealersLocation
        dealers={dealers}
        onClick={onSelectDealer}
        searchValue={searchValue}
        setSelectDealer={setSelectDealer}
        setSearchValue={setSearchValue}
        setDealerLocation={setDealerLocation}
        onClickCurrentLocation={onClickCurrentLocation}
        onClearSearchType={onClearSearchType}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpenFilter,
    selectedDealer,
    dealers,
    searchValue,
    selectedOptions,
    allSearchKey,
  ]);

  return (
    <div
      className={cn(
        className,
        'ease-bounce transition-transform duration-300',
        {
          [styles.xlCollapseDrawer]: !isOpenCollapsePanel,
        }
      )}
    >
      {!!selectedDealer && (
        <CollapsePanelButton
          isOpen={isOpenCollapsePanel}
          onClick={toggleCollapsePanel}
        />
      )}

      {renderContent}
      <FilterOptions
        isOpenFilter={isOpenFilter}
        onToggleFilter={onToggleFilter}
        selectedOptions={selectedOptions}
        onSelectOptions={handleSelectFilterOption}
        onSetNewOptions={onSelectType}
        onSearch={onFilterSearch}
        onReset={onResetFilterSearch}
        hideFilter={!!selectedDealer}
        className={cn('ease-bounce transition-opacity duration-300', {
          'opacity-0': !isOpenCollapsePanel || !!selectedDealer,
        })}
      />
    </div>
  );
};
